import type { vitality as en } from '@/i18n/locales/en/vitality';
import type { Translation } from '@/i18n/types';

export const vitality: Translation<typeof en> = {
  page: {
    title: 'Vitalità',
    description: 'Tendenze di benessere anonimizzate e indicative a livello di gruppo.',
    noScope: "Seleziona un'azienda per vedere i suoi dati di vitalità.",
    noDepartments: 'Nessun reparto qui ha statistiche che puoi vedere.',
    disclaimer:
      'Tutti i valori sono <strong>indicazioni di benessere</strong> e <strong>non costituiscono una diagnosi medica</strong>. Le tendenze vengono mostrate solo con un numero sufficiente di partecipanti (privacy by design).',
  },
  filters: {
    title: 'Filters',
    department: {
      label: 'Reparto',
    },
    location: {
      label: 'Sede',
      all: 'Tutte le sedi',
    },
  },
  badge: {
    measured: 'Misurato',
    aiDerived: 'Analisi IA',
  },
  level: {
    good: 'Buono',
    opportunity: 'Opportunità',
  },
  statusPill: {
    healthy: 'Livello sano',
    opportunity: 'Occasione di aiutare',
    strongForward: 'Forte e in crescita',
  },
  trend: {
    flat: 'Nessuna variazione',
    vsPrevious: 'rispetto al periodo precedente',
    vsAverage: 'rispetto alla media del periodo',
    up: 'In crescita',
    down: 'Leggermente più basso',
    upBad: 'Leggermente più alto',
    week: 'sett.',
  },
  units: {
    hoursMinutes: '{{h}}h {{m}}m',
    average: 'media {{value}}',
  },
  status: {
    noData: 'Nessun dato in questo periodo.',
  },
  table: {
    level: 'Livello',
    trend: 'Tendenza',
    action: 'Azione',
    actionNone: '—',
    actionLink: 'Reparto',
    actionLinkTitle: 'Vai a questo reparto',
  },
  insight: {
    title: 'Cosa dicono i dati di vitalità',
    unavailable: 'Non ci sono ancora abbastanza dati per un riepilogo.',
    vitality: {
      up: 'La vitalità è in crescita ({{pct}}% {{period}}) con un tasso di partecipazione del {{participation}}%.',
      down: 'La vitalità è in calo ({{pct}}% {{period}}) con un tasso di partecipazione del {{participation}}%.',
      flat: 'La vitalità è stabile ({{pct}}% {{period}}) con un tasso di partecipazione del {{participation}}%.',
      noParticipation: 'La vitalità è stabile ({{pct}}% {{period}}).',
    },
    period: {
      last7Days: 'negli ultimi 7 giorni',
      last30Days: 'questo mese',
      thisWeek: 'questa settimana',
      thisMonth: 'questo mese',
      custom: 'in questo periodo',
    },
    stress: {
      up: 'Lo stress è in aumento ({{pct}}%)',
      down: 'lo stress sta diminuendo ({{pct}}%)',
      flat: 'lo stress resta stabile',
    },
    sleep: {
      longer: 'e i dipendenti dormono in media {{minutes}} min in più.',
      shorter: 'e i dipendenti dormono in media {{minutes}} min in meno.',
      same: 'e i dipendenti dormono circa la stessa quantità.',
    },
    mostActiveDay: '{{day}} è il giorno più attivo.',
  },
  cards: {
    vitalityScore: {
      title: 'Punteggio di vitalità',
      unit: '%',
      info: 'Un punteggio composito basato su sonno, attività, HRV, stress e frequenza cardiaca a riposo.',
    },
    sleep: {
      title: 'Sonno',
      unit: '',
      info: 'Durata media del sonno per notte.',
    },
    stress: {
      title: 'Stress',
      unit: '%',
      info: 'Livello di stress misurato; più basso è, più si è calmi.',
    },
    recovery: {
      title: 'Recupero',
      unit: '%',
      info: 'Quanto bene si riprende il corpo, in base a sonno, HRV e stress.',
    },
    activity: {
      title: 'Attività',
      unit: 'passi/giorno',
      info: 'Numero medio di passi al giorno.',
    },
    hrv: {
      title: 'HRV',
      unit: 'ms',
      info: 'Variabilità della frequenza cardiaca — un indicatore di recupero e resilienza.',
    },
    restingHeartRate: {
      title: 'Frequenza cardiaca a riposo',
      unit: 'bpm',
      info: 'La frequenza cardiaca più bassa misurata nel periodo selezionato, come approssimazione della frequenza cardiaca a riposo.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'La vitalità si sta sviluppando bene — mantieni le routine attuali.',
      opportunity:
        "C'è margine di miglioramento sulla vitalità — un breve confronto con il team può far emergere cosa la ostacola.",
    },
    sleep: {
      good: 'Il sonno del team appare sano — nessuna azione necessaria.',
      opportunity:
        "Il sonno è sotto l'obiettivo — valuta un promemoria sulla routine serale e meno schermi prima di dormire.",
    },
    stress: {
      good: 'Il livello di stress è sotto controllo — continua a monitorare.',
      opportunity:
        "Lo stress è elevato — un breve confronto sul carico di lavoro o un'attività di recupero potrebbe aiutare.",
    },
    recovery: {
      good: 'Il recupero è solido — il team si riprende bene dopo gli sforzi.',
      opportunity:
        'Il recupero è in ritardo — verifica se i giorni di riposo e il carico di lavoro sono in equilibrio.',
    },
    activity: {
      good: 'Il livello di attività è sano — nessuna azione necessaria.',
      opportunity:
        "L'attività è sotto l'obiettivo — una riunione camminando o una sfida a passi potrebbe aiutare.",
    },
    hrv: {
      good: "L'HRV appare sana — nessuna azione necessaria.",
      opportunity:
        "L'HRV è più bassa del previsto — spesso è legata a stress o sonno scarso; vale la pena parlarne insieme.",
    },
    restingHeartRate: {
      good: 'La frequenza cardiaca a riposo è in un intervallo sano.',
      opportunity:
        'La frequenza cardiaca a riposo è elevata — insieme a stress o sonno ridotto, vale la pena segnalarlo.',
    },
  },
};
